<script>
  import rules from '@/helpers/validation-rules';
  import headers from '@/views/app/dashboard/extra/data/refer/headers';

  import ShareLinkDialog from '@/components/ShareLinkDialog.vue';
  import YellowButton from '@/components/base/YellowButton.vue';
  import IconUsers from '@/assets/images/svg/icons/users.svg';
  import IconCoins from '@/assets/images/svg/icons/coins.svg';

  export default {
    name: 'ReferFriends',

    metaInfo: {
      title: 'ReferFriends',
    },

    components: {
      YellowButton,
      ShareLinkDialog,
      IconUsers,
      IconCoins,
    },

    data: () => ({
      headers,
      rules,
      loading: false,

      isInputValid: [false],
      friendsToRefer: [''],
      showShareLinkDialog: false,
      showTooltip: false,

      bonusToBalanceDialog: false,

      queue: [],
      isProcessing: false,
    }),

    computed: {
      userReferralRewardsBalance() {
        return this.$store.state.userReferralRewardsData.balance;
      },

      referralLink() {
        return this.$store.getters.getReferralLink;
      },

      referralRewardsBalance() {
        return this.$store.getters.getReferralRewardsData.balance / 100 || 0;
      },

      totalReferrals() {
        return this.$store.getters.getReferralRewardsData.count || 0;
      },
    },

    async mounted() {
      await Promise.all([
        this.$store.dispatch('getReferralRewardsData'),
        this.$store.dispatch('loadGlobalAffiliateConfig'),
      ]);
    },

    methods: {
      goToTNC() {
        this.$router.push('/tnc');
      },

      async generateReferralLink() {
        if (this.referralLink !== '') {
          this.showAlert({ text: 'Referral Link already generated' });
        } else {
          let referralLinkGenerated = await this.$store.dispatch('generateReferralLink');

          if (referralLinkGenerated !== '') {
            this.showAlert({
              title: 'Thank you for joining the program!',
              text: 'Please find your referral URL below',
            });
          } else {
            this.showAlert({
              text: 'Error generating referral link.<br />Please try again!',
              ctaText: 'Generate Link Again',
              ctaAction: this.generateReferralLink,
            });
          }
        }
      },

      showAlert({ title = '', text = '', ctaText = '', ctaAction = null }) {
        const data = {
          title: title,
          text: text,
          theme: 'white',
          showOverlay: true,
          permanent: true,
        };

        if (ctaText && typeof ctaAction === 'function') {
          data.ctaText = ctaText;
          data.ctaAction = ctaAction;
        }

        this.$store.dispatch('showAlertDialog', data);
      },

      queueInviteFriend(email, index) {
        this.queue.push({ email, index });
        this.processQueue();
      },

      async processQueue() {
        if (this.isProcessing || this.queue.length === 0) return;
        this.isProcessing = true;

        const { email, index } = this.queue.shift();
        await this.inviteFriend(email, index);

        this.isProcessing = false;
        this.processQueue();
      },

      async inviteFriend(email, index) {
        if (this.referralLink === '' || this.referralLink === undefined) {
          this.showAlert({
            text: 'Please generate referral link first!',
            ctaText: 'Generate Referral Link',
            ctaAction: this.generateReferralLink,
          });
          return;
        }

        let response = await this.$store.dispatch('referFriend', {
          email: email,
          referralLink: this.referralLink,
        });

        this.friendsToRefer[index] = '';
        const [form] = this.$refs[`form-${index}`];

        form.resetValidation();
        this.$forceUpdate();

        if (response.error) {
          if (response.errorMessage.hasError) {
            this.showAlert({ text: response.errorMessage.message });
          } else {
            this.showAlert({ text: 'An error occurred. Please try again later.' });
          }
        } else {
          this.showAlert({ text: response.message });
        }
      },

      clickHandler(task) {
        this.addToQueue(task);
      },

      shareLinkModalOpen() {
        if (this.referralLink !== '' && this.referralLink !== undefined) {
          this.showShareLinkDialog = true;
        } else {
          this.showAlert({
            text: 'Please generate referral link first!',
            ctaText: 'Generate Referral Link',
            ctaAction: this.generateReferralLink,
          });
        }
      },

      async claimReward() {
        if (this.referralRewardsBalance <= 0) return;
        await this.$store.dispatch('transferRewardToBalance', this.referralRewardsBalance);
      },
    },
  };
</script>

<template>
  <div class="refer-friends-background position-relative">
    <div class="bg-wrapper position-absolute left-0 top-0 w-100 h-100 pt-6 px-10 pb-15">
      <img class="w-100 h-100 object-cover" src="/images/refer-a-friend/background.webp?v=1" alt="Background" />
    </div>
    <div class="refer-friends-wrapper">
      <div class="refer-friends-page">
        <div class="content-sections">
          <section class="page-header">
            <h2 class="page-title">
              Refer a
              <span class="gold--text">Friend</span>
            </h2>

            <p class="page-subtitle">
              MaxQuest is always more fun with friends. Earn
              <span class="gold--text">10% commission</span>
              on each referred friend’s prize multiplier during their first 30 days.
            </p>

            <p class="page-lead">
              Inviting your friends is quick and easy.
              <br />
              Simply click '
              <strong>Generate Referral Link</strong>
              ' and share it with them via email or social media.
            </p>

            <YellowButton class="generate-ref-button" @click="generateReferralLink">
              Generate Referral Link
            </YellowButton>
          </section>

          <section class="share-link">
            <div class="share-link-text">My referral link:</div>
            <div class="share-link-input">
              <v-text-field
                v-model="referralLink"
                class=""
                hide-details
                placeholder="A referral link will be inserted here"
                readonly
                solo
              />
              <YellowButton @click="shareLinkModalOpen">Share</YellowButton>
            </div>
          </section>

          <section class="friends-section">
            <div class="friends-section-text">Refer friends via email:</div>
            <div class="friends-section-input">
              <v-form
                v-for="(item, index) in friendsToRefer"
                :key="index"
                :ref="`form-${index}`"
                v-model="isInputValid[index]"
                class="d-flex w-full"
              >
                <v-text-field
                  v-model="friendsToRefer[index]"
                  :rules="[rules.email()]"
                  class="referral-input"
                  placeholder="Enter your friend’s email address here"
                  solo
                />
                <YellowButton
                  :disabled="!isInputValid[index] || isProcessing"
                  class="invite-button"
                  theme="outlined"
                  @click="queueInviteFriend(item, index)"
                >
                  Invite
                </YellowButton>
              </v-form>
            </div>
          </section>

          <section class="stats">
            <div class="stats-container stats-friends">
              <div class="stats-container-left-side">
                <IconUsers />
                Referred Friends:
              </div>
              <div class="stats-amount">{{ totalReferrals || 0 }}</div>
            </div>
            <div class="stats-container stats-balance">
              <div class="stats-container-left-side">
                <IconCoins />
                Commission Balance:
              </div>
              <div class="stats-amount">${{ referralRewardsBalance }}</div>
            </div>
            <YellowButton :disabled="referralRewardsBalance <= 0" class="claim-button" @click="claimReward">
              Claim Commission
            </YellowButton>
          </section>

          <p class="terms" @click="goToTNC">Terms & Conditions</p>
        </div>

        <!--        <div v-if="$vuetify.breakpoint.width >= 768 && $vuetify.breakpoint.width < 1024" class="stars-background" />-->

        <div class="art-section">
          <!--          <div v-if="$vuetify.breakpoint.width >= 1024" class="stars-background" />-->
          <!--          <div class="space-ring" />-->
          <img alt="astronaut 1" class="astronaut astronaut--first" src="/images/effects/astronaut_1.webp?v=1" />
          <img alt="astronaut 2" class="astronaut astronaut--second" src="/images/effects/astronaut_2.webp?v=1" />
        </div>
      </div>

      <ShareLinkDialog v-model="showShareLinkDialog" :referralLink="referralLink" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import 'src/scss/mixins';
  @import 'src/scss/variables';

  @keyframes first-astronaut-levitating {
    0% {
      transform: translateX(-90%) translateY(-16px) rotate(0);
    }
    24% {
      transform: translateX(-100%) translateY(-25px) rotate(-10deg);
    }
    51% {
      transform: translateX(-90%) translateY(-32px) rotate(3deg);
    }
    75% {
      transform: translateX(-80%) translateY(0) rotate(10deg);
    }
    100% {
      transform: translateX(-90%) translateY(-16px) rotate(0);
    }
  }

  @keyframes second-astronaut-levitating {
    0% {
      transform: translateX(-14%) translateY(-24px) rotate(0);
    }
    27% {
      transform: translateX(-18%) translateY(-30px) rotate(-15deg);
    }
    49% {
      transform: translateX(-14%) translateY(-20px) rotate(-12deg);
    }
    75% {
      transform: translateX(-10%) translateY(-15px) rotate(0);
    }
    100% {
      transform: translateX(-14%) translateY(-24px) rotate(0);
    }
  }

  .bg-wrapper {
    img {
      border-radius: 24px;
    }
  }

  .refer-friends-wrapper {
    @include basic-spacings;
    padding-top: 24px;
    padding-bottom: 60px;

    @media #{$viewport-tablet} {
      padding: 24px 48px 48px 24px;
    }

    @media screen and (min-width: 1281px) {
      width: 100%;
      max-width: calc(100vw - 389px - 64px);
      margin-inline: auto;
      padding-inline: 0;
    }

    @media screen and (min-width: 1842px) {
      max-width: 1448px;
      padding-right: 32px;
    }
  }

  .refer-friends-page {
    position: relative;
    height: 100%;
    border-radius: var(--Spacing-xxl, 24px);
    //background: linear-gradient(270deg, rgba(28, 6, 48, 0) 42.5%, #1c0630 100%);
    //overflow: hidden;

    @media #{$viewport-tablet} {
      position: relative;
      display: flex;
      border-radius: var(--Spacing-xxl, 24px);

      &:before {
        content: '';
        position: absolute;
        z-index: 2;
        inset: 0;
        //background: linear-gradient(270deg, rgba(28, 6, 48, 0) 42.5%, #1c0630 100%);
      }
    }

    .content-sections {
      position: relative;
      z-index: 3;
      padding: 40px 24px 0 24px;
      display: flex;
      flex-direction: column;
      gap: 24px;

      @media #{$viewport-tablet} {
        padding: 60px;
        //max-width: 70%;
      }

      @media #{$viewport-desktop} {
        padding: 60px;
        max-width: 50%;
      }
    }

    .page-header {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      gap: 24px;
    }

    .page-title {
      color: var(--Colors-White, #fff);
      font-size: 40px;
      font-weight: 700;
      line-height: normal;
      margin: 0;

      @media #{$viewport-tablet} {
        font-size: 48px;
      }
    }

    .page-subtitle {
      color: var(--Colors-White, #fff);
      font-size: 16px;
      font-weight: 500;
      line-height: 24px;
      margin: 0;

      span {
        font-weight: 600;
      }

      @media #{$viewport-tablet} {
        font-size: 20px;
      }
    }

    .page-lead {
      color: var(--Colors-White, #fff);
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
      margin: 0;
    }

    .generate-ref-button {
      min-height: 58px;
      color: #1e1e1e;
      font-size: 16px;
      font-weight: 600;
      line-height: 26px;
    }

    .share-link {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      gap: 8px;
    }

    .share-link-text {
      color: var(--Colors-White, #fff);
      font-size: 14px;
      font-weight: 500;
      line-height: 21px;
    }

    .share-link-input,
    .friends-section-input {
      display: flex;
      align-items: stretch;
      gap: 8px;
      height: 44px;
      width: 100%;

      .v-form {
        gap: 8px;
      }

      .yellow-button {
        width: 96px;
        font-size: 16px;
        font-weight: 600;
        line-height: 26px;
      }

      &::v-deep {
        .v-input__control,
        .v-input__slot {
          height: 100%;
          min-height: 44px;
        }

        .v-input__slot {
          border-radius: var(--Spacing-m, 8px);
          border: 1px solid rgba(255, 255, 255, 0.6);
          background: #160526;
          box-shadow: none;

          input {
            color: #96d7ff;
            font-size: 14px;
            font-weight: 400;
            line-height: 150%;
            padding-left: 0 !important;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }

        input::placeholder {
          color: rgba(255, 255, 255, 0.8);
        }
      }
    }

    .referral-input {
      &::v-deep {
        &.error--text {
          .v-input__slot {
            border-color: red;
          }

          input {
            color: red !important;
          }
        }

        .v-input__control {
          position: relative;
        }

        .v-text-field__details {
          position: absolute;
          bottom: -18px;
          padding: 0;

          .v-messages {
            font-size: 13px;
            line-height: 1;
            font-style: normal;
          }
        }
      }
    }

    .friends-section {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      gap: 8px;
    }

    .friends-section-text {
      color: var(--Colors-White, #fff);
      font-size: 14px;
      font-weight: 500;
      line-height: 1;
    }

    .invite-button {
      font-size: 16px;
      font-weight: 600;
      line-height: 26px;

      &[disabled] {
        color: var(--Colors-White, #fff);
      }
    }

    .stats {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .stats-container {
      display: flex;
      align-items: stretch;
      justify-content: space-between;
      height: 44px;
      padding: 10px 16px;
      border-radius: var(--Spacing-m, 8px);
      border: 1px solid rgba(255, 255, 255, 0.6);
      background: rgba(0, 0, 0, 0.2);
    }

    .stats-container-left-side {
      display: flex;
      gap: 8px;
      width: 100%;
      color: var(--Colors-White, #fff);
      font-size: 16px;
      font-weight: 400;
      line-height: 150%;

      svg {
        max-width: 22px;
      }
    }

    .stats-amount {
      color: #f8bf1e;
      font-size: 20px;
      font-weight: 600;
      line-height: 1;
    }

    .claim-button {
      height: 50px;
      color: #2d2d2d;
      font-size: 16px;
      font-weight: 600;
      line-height: 26px;

      &[disabled] {
        color: var(--Colors-White, #fff);
      }
    }

    .terms {
      cursor: pointer;
      text-align: center;
      color: var(--Colors-White, #fff);
      font-size: 14px;
      font-weight: 500;
      line-height: normal;
      text-decoration-line: underline;
    }
  }

  //.stars-background {
  //  position: absolute;
  //  z-index: 1;
  //  top: 0;
  //  bottom: 0;
  //  right: 0;
  //  left: auto;
  //  width: 60%;
  //  background: url(/images/refer?v=2) transparent 0 0 / cover no-repeat;
  //  mix-blend-mode: lighten;
  //  opacity: 0.5;
  //
  //  @media #{$viewport-desktop} {
  //    opacity: 0.8;
  //    inset: 0;
  //    width: auto;
  //  }
  //}

  .art-section {
    container: space / inline-size;
    position: relative;
    width: 100%;
    aspect-ratio: 490/224;
    margin: 0;
    //overflow: hidden;
    right: 0;

    @include respond(desktop-down) {
      //position: absolute;
      //right: 0;
      display: none;
    }

    @media #{$viewport-desktop} {
      width: 61%;
    }

    @media #{$viewport-desktop-large} {
      width: 58%;
    }

    .space-ring {
      position: absolute;
      bottom: 0;
      width: 100%;
      aspect-ratio: 490/193;
      background: url(/images/effects/space-ring-mobile.webp?v=2) lightgray 50% / cover no-repeat;
      mix-blend-mode: lighten;

      @media #{$viewport-tablet} {
        bottom: auto;
        top: 180px;
        aspect-ratio: 2264/1380;
        background: url(/images/effects/space-ring-desktop.webp?v=2) transparent 50% / cover no-repeat;
      }
    }

    .astronaut {
      position: absolute;
      z-index: 2;
      top: 10px;
      will-change: transform;
      user-select: none;

      @media #{$viewport-tablet} {
        top: 180px;
      }

      @media #{$viewport-desktop} {
        top: 160px;
      }

      @media #{$viewport-desktop-large} {
        top: 150px;
      }

      @media screen and (min-width: 1800px) {
        top: 120px;
      }

      &--first {
        aspect-ratio: 737/894;
        margin-left: 50%;
        animation: first-astronaut-levitating 15s ease infinite;
      }

      &--second {
        aspect-ratio: 873/1106;
        margin-left: 50%;
        animation: second-astronaut-levitating 16s ease infinite;
      }
    }
  }

  @container space (min-width: 200px) {
    .astronaut--first {
      max-width: 37.5cqi;
    }

    .astronaut--second {
      max-width: 50.5cqi;
    }
  }

  @container space (min-width: 416px) {
    .astronaut--first {
      @media screen and (max-width: 1023px) {
        left: -40px;
        max-width: 30cqi;
      }

      @media #{$viewport-desktop} {
        max-width: 40cqi;
      }

      @media #{$viewport-desktop-large} {
        max-width: 36cqi;
      }
    }

    .astronaut--second {
      @media screen and (max-width: 1023px) {
        left: -40px;
        max-width: 34cqi;
      }

      @media #{$viewport-desktop} {
        max-width: 47cqi;
      }

      @media #{$viewport-desktop-large} {
        max-width: 43cqi;
      }
    }
  }
</style>
